import { getAppScheme } from './app-scheme';

export type UaePassNativeResume = {
  status?: string;
  url: string;
};

type UaePassNativeResumeListener = (resume: UaePassNativeResume) => void;

let pendingResume: UaePassNativeResume | null = null;
let waitingForOAuthCallback = false;
const listeners = new Set<UaePassNativeResumeListener>();
const publishedResumeKeys = new Set<string>();
const handledResumeKeys = new Set<string>();

const MAX_TRACKED_RESUMES = 12;
const UAE_PASS_NATIVE_FAILURE_STATUSES = new Set([
  'access_denied',
  'cancel',
  'cancelledonapp',
  'cancelled',
  'canceled',
  'fail',
  'failed',
  'failure',
  'invalid_request',
  'login_required',
]);

function normalizeResumeStatusValue(value?: string | null) {
  const normalized = value?.trim().toLowerCase();

  if (!normalized) {
    return undefined;
  }

  if (UAE_PASS_NATIVE_FAILURE_STATUSES.has(normalized)) {
    return 'failure';
  }

  if (normalized === 'succeeded') {
    return 'success';
  }

  return normalized;
}

function readStatusFromResumeUrl(value: string) {
  const parsed = parseResumeUrl(value);

  if (!parsed) {
    return undefined;
  }

  return (
    normalizeResumeStatusValue(parsed.searchParams.get('status')) ??
    normalizeResumeStatusValue(parsed.searchParams.get('error'))
  );
}

export function resolveUaePassNativeResumeStatus(
  status: string | undefined,
  url: string,
) {
  return normalizeResumeStatusValue(status) ?? readStatusFromResumeUrl(url);
}

function normalizeResume(resume: UaePassNativeResume) {
  return {
    ...resume,
    status: resolveUaePassNativeResumeStatus(resume.status, resume.url),
  };
}

function normalizeResumeStatus(status?: string) {
  return status?.trim().toLowerCase() || 'unknown';
}

function parseResumeUrl(value: string) {
  try {
    return new URL(value);
  } catch {
    try {
      return new URL(decodeURIComponent(value));
    } catch {
      return null;
    }
  }
}

export function decodeUaePassResumeUrlParam(value?: string | null) {
  if (!value) {
    return null;
  }

  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    return globalThis.atob(padded);
  } catch {
    return null;
  }
}

function normalizeResumeUrlForKey(value: string) {
  const parsed = parseResumeUrl(value);

  if (!parsed) {
    return value;
  }

  const sessionDataKey = parsed.searchParams.get('sessionDataKey');
  const status = parsed.searchParams.get('status');
  const relyingParty = parsed.searchParams.get('relyingParty');

  if (sessionDataKey) {
    return [
      parsed.origin,
      parsed.pathname,
      sessionDataKey,
      status ?? '',
      relyingParty ?? '',
    ].join(':');
  }

  return parsed.toString();
}

function getResumeKey(resume: UaePassNativeResume) {
  return `${normalizeResumeStatus(resume.status)}:${normalizeResumeUrlForKey(resume.url)}`;
}

function rememberResumeKey(collection: Set<string>, key: string) {
  if (collection.has(key)) {
    return false;
  }

  collection.add(key);

  if (collection.size > MAX_TRACKED_RESUMES) {
    const oldestKey = collection.values().next().value;
    if (oldestKey) {
      collection.delete(oldestKey);
    }
  }

  return true;
}

function normalizeNativePath(parsed: URL) {
  const pathname = parsed.pathname || '/';

  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    return pathname;
  }

  return parsed.host ? `/${parsed.host}${pathname}` : pathname;
}

export function parseUaePassNativeResumeUrl(value: string) {
  let parsed: URL;

  try {
    parsed = new URL(value, `${getAppScheme()}://`);
  } catch {
    return null;
  }

  const normalizedPath = normalizeNativePath(parsed);
  const hasResumeUrlParam =
    parsed.searchParams.has('resumeUrl') ||
    parsed.searchParams.has('url') ||
    parsed.searchParams.has('resumeUrlEncoded') ||
    parsed.searchParams.has('urlEncoded');
  const isResumePath =
    normalizedPath.includes('uae-pass/native-resume') ||
    normalizedPath.includes('uae-pass/resume') ||
    (normalizedPath === '/uae-pass' && hasResumeUrlParam);

  if (!isResumePath) {
    return null;
  }

  const url =
    decodeUaePassResumeUrlParam(
      parsed.searchParams.get('resumeUrlEncoded') ??
        parsed.searchParams.get('urlEncoded'),
    ) ??
    parsed.searchParams.get('resumeUrl') ??
    parsed.searchParams.get('url');

  if (!url) {
    return null;
  }

  return {
    status: resolveUaePassNativeResumeStatus(
      parsed.searchParams.get('resumeStatus') ??
        parsed.searchParams.get('status') ??
        undefined,
      url,
    ),
    url,
  } satisfies UaePassNativeResume;
}

export function claimUaePassNativeResume(resume: UaePassNativeResume) {
  return rememberResumeKey(
    handledResumeKeys,
    getResumeKey(normalizeResume(resume)),
  );
}

export function markUaePassNativeResumeWaitingForCallback() {
  waitingForOAuthCallback = true;
}

export function hasUaePassNativeResumeWaitingForCallback() {
  return waitingForOAuthCallback;
}

export function clearUaePassNativeResumeWaitingForCallback() {
  waitingForOAuthCallback = false;
}

export function resetUaePassNativeResumeState() {
  pendingResume = null;
  waitingForOAuthCallback = false;
  publishedResumeKeys.clear();
  handledResumeKeys.clear();
}

export function publishUaePassNativeResume(resume: UaePassNativeResume) {
  const normalizedResume = normalizeResume(resume);
  const resumeKey = getResumeKey(normalizedResume);

  if (
    publishedResumeKeys.has(resumeKey) ||
    handledResumeKeys.has(resumeKey) ||
    !rememberResumeKey(publishedResumeKeys, resumeKey)
  ) {
    return false;
  }

  if (listeners.size === 0) {
    pendingResume = normalizedResume;
    return true;
  }

  pendingResume = null;
  listeners.forEach((listener) => listener(normalizedResume));
  return true;
}

export function takePendingUaePassNativeResume() {
  const resume = pendingResume;
  pendingResume = null;
  return resume;
}

export function addUaePassNativeResumeListener(
  listener: UaePassNativeResumeListener,
) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
